import axios from 'axios';
import http from 'config/http';

import { PublicAPIStatusEnum } from 'api/enums';
import { PluginModel, ProcoreModel, PublicApiIngestionModel, UserModel } from 'api/models';
import { ExternalCameraModel } from 'api/models/external-cameras.model';
import { PublicApiModel } from 'api/models/public-api.model';
import { getApiUrl } from 'config/api-url';

export const getAllPlugins = async () => {
    const { data } = await http.get(`${getApiUrl()}/plugins`);

    return PluginModel.fromObjectList(data);
};

export const getPluginById = async (pluginId: string) => {
    const { data } = await http.get(`${getApiUrl()}/plugins/details/${pluginId}`);

    return PluginModel.fromObject(data);
};

export const getWhatsAppBotPluginDetails = async () => {
    try {
        const { data } = await http.get(`${getApiUrl()}/plugins/whatsapp-bot`);

        return data as Pick<UserModel, 'phone'>;
    } catch {
        return null;
    }
};

export const activateWhatsAppBot = async (payload: Pick<UserModel, 'phone'>) => {
    const { data } = await http.post(`${getApiUrl()}/plugins/whatsapp-bot`, payload);

    return data as Pick<UserModel, 'phone'>;
};

export const updateWhatsAppBot = async (payload: Pick<UserModel, 'phone'>) => {
    const { data } = await http.put(`${getApiUrl()}/plugins/whatsapp-bot`, payload);

    return data as Pick<UserModel, 'phone'>;
};

export const deactivateWhatsAppBot = async () => {
    await http.delete(`${getApiUrl()}/plugins/whatsapp-bot`);
};

export const requestDeveloperAccess = async () => {
    await http.post(`${getApiUrl()}/plugins/public-api/enable-developer-access`);
};

export const getPublicApiPluginDetails = async () => {
    try {
        const { data } = await http.get(`${getApiUrl()}/plugins/public-api`);

        return {
            data: PublicApiModel.fromObjectList(data.data),
            requestStatus: data.requestStatus as PublicAPIStatusEnum,
            url: data.url as string,
        };
    } catch (err: any) {
        return null;
    }
};

export const activatePublicApi = async (payload: { title: string }) => {
    const { data } = await http.post(`${getApiUrl()}/plugins/public-api/activate-developer-access`, payload);

    return PublicApiModel.fromObject(data);
};

export const deactivatePublicApi = async (publicApiId: string) => {
    await http.post(`${getApiUrl()}/plugins/public-api/revoke-developer-access`, {
        id: publicApiId,
    });
};

export const regeneratePublicApi = async (publicApiId: string) => {
    const { data } = await http.post(`${getApiUrl()}/plugins/public-api/regenerate-access-keys`, { id: publicApiId });

    // return data as PublicApiModel['details'];
    return PublicApiModel.cloneFrom(data);
};

export const activateProcoreEmbedded = async () => {
    const { data } = await http.post(`${getApiUrl()}/plugins/procore`);

    return ProcoreModel.fromObject(data);
};

export const getProcore = async () => {
    try {
        const { data } = await http.get(`${getApiUrl()}/plugins/procore`);

        return ProcoreModel.fromObject(data);
    } catch {
        return null;
    }
};

export const deactivateProcoreEmbedded = async () => {
    await http.delete(`${getApiUrl()}/plugins/procore`);
};

export const getAuthTokenWithProcore = async (token: string) => {
    const { data } = await axios.get(`${getApiUrl()}/procore/embedded`, {
        params: { t: token },
    });

    return { token: data.token as string };
};

export const activatePublicApiIngestion = async (vendor: string) => {
    const { data } = await http.post(`${getApiUrl()}/public-plugins/${vendor}`);

    return PublicApiIngestionModel.fromObject(data);
};

export const deactivatePublicApiIngestion = async (vendor: string) => {
    await http.delete(`${getApiUrl()}/public-plugins/${vendor}`);
};

export const getPublicApiIngestion = async (vendor: string) => {
    try {
        const { data } = await http.get(`${getApiUrl()}/public-plugins/${vendor}`);

        return PublicApiIngestionModel.fromObject(data);
    } catch {
        return null;
    }
};

export const updateCameraPublicIngestionApi = async (vendor: string, payload: { cameraIds: string[] }) => {
    const { data } = await http.put(`${getApiUrl()}/public-plugins/${vendor}/cameras`, {
        payload,
    });

    return PublicApiIngestionModel.fromObject(data);
};

export const regeneratePublicApiIngestion = async (vendor: string) => {
    const { data } = await http.post(`${getApiUrl()}/public-plugins/${vendor}/regenerate-keys/`);

    return PublicApiIngestionModel.fromObject(data);
};

export const createExternalCamera = async (projectId: string, payload: { name: string; vendor: string }) => {
    const { data } = await http.post(`${getApiUrl()}/projects/${projectId}/external-cameras`, payload);

    return data;
};

export const getExternalCameras = async (queryParams: { vendor: string }) => {
    const { data } = await http.get(`${getApiUrl()}/external-cameras`, {
        params: queryParams,
    });

    return ExternalCameraModel.fromObjectList(data);
};

export const deleteExternalCamera = async (cameraId: string, queryParams: { vendor: string }) => {
    await http.delete(`${getApiUrl()}/external-cameras/${cameraId}`, {
        params: queryParams,
    });
};
